import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Upload, X, FileText, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCATION_TYPES = [
  { value: "from_location", label: "From Location (Pickup)" },
  { value: "warehouse",     label: "Warehouse" },
  { value: "to_location",   label: "To Location (Delivery)" },
];

const TODAY = format(new Date(), "yyyy-MM-dd");

const EMPTY_FORM = {
  transfer_date:            TODAY,
  consignor_name:           "",
  consignor_phone:          "",
  pickup_address:           "",
  consignee_name:           "",
  consignee_phone:          "",
  drop_address:             "",
  drop_pincode:             "",
  current_location:         "from_location",
  description:              "",
  quantity:                 "",
  to_pay_amount:            "",
  insurance_opted:          "NO",
  risk_coverage_type:       "Owner's Risk",
  eway_bill_no:             "NA",
  booking_incharge:         "",
  documents:                [],
  origin_type:              "from_location",
  origin_warehouse_id:      "",
  destination_type:         "to_location",
  destination_warehouse_id: "",
  load_ids:                 [],
  consignment_id:           "",
  // legacy fields kept for backward compat
  value_of_consignment:     "",
  freight_to_pay:           true,
  amount_at_loading:        "",
  amount_at_delivery:       "",
  transit_insurance:        false,
  owner_risk:               true,
  carrier_risk:             false,
};

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-3 pt-1">
    <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#94A3B8] shrink-0">{children}</span>
    <div className="flex-1 h-px bg-[#E2E8F0]" />
  </div>
);

const FL = ({ children, locked }) => (
  <label className="flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1">
    {children}{locked && <Lock size={9} className="text-[#94A3B8]" title="Admin only" />}
  </label>
);

const ReadOnly = ({ value }) => (
  <div className="w-full border border-[#E2E8F0] bg-[#F9FAFB] px-3 h-10 flex items-center text-sm text-[#475569]">
    {value || "—"}
  </div>
);

const TI = ({ value, onChange, placeholder, type = "text", ...rest }) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} {...rest}
    className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 h-10 text-sm" />
);

function resolveLabel(type, warehouseId, booking, warehouses) {
  if (type === "warehouse") return warehouses.find(w => w.id === warehouseId)?.warehouse_name || "";
  if (!booking) return "";
  return type === "from_location"
    ? (booking.from_location || booking.moving_from || "")
    : (booking.to_location   || booking.moving_to   || "");
}

export function CreateTransferDialog({ open, onClose, initialBooking, onCreated, userRole }) {
  const isAdmin = ["admin", "superadmin", "manager"].includes(userRole);

  const [bookings,          setBookings]          = useState([]);
  const [selBooking,        setSelBooking]        = useState(null);
  const [loads,             setLoads]             = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [warehouses,        setWarehouses]        = useState([]);
  const [form,              setForm]              = useState(EMPTY_FORM);
  const [saving,            setSaving]            = useState(false);
  const fileInputRef = useRef(null);

  // ── Fetch warehouses + trips on open ────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const calls = [
      axios.get(`${API}/warehouses`, { withCredentials: true }),
    ];
    if (!initialBooking) calls.push(axios.get(`${API}/confirmed-leads`, { withCredentials: true }));
    Promise.all(calls).then(([wRes, bRes]) => {
      setWarehouses(wRes.data);
      if (!initialBooking && bRes) setBookings(bRes.data);
    }).catch(() => {});
  }, [open]);

  // ── Set booking from prop ────────────────────────────────────────────────
  useEffect(() => {
    if (open && initialBooking) setSelBooking(initialBooking);
  }, [open, initialBooking]);

  // ── Fetch loads + additional charges when booking changes ───────────────
  useEffect(() => {
    if (!open || !selBooking) { setLoads([]); setAdditionalCharges([]); return; }
    Promise.all([
      axios.get(`${API}/confirmed-leads/${selBooking.id}/loads`,              { withCredentials: true }),
      axios.get(`${API}/confirmed-leads/${selBooking.id}/additional-charges`, { withCredentials: true }),
    ]).then(([lRes, cRes]) => {
      setLoads(lRes.data);
      setAdditionalCharges(cRes.data);
    }).catch(() => { setLoads([]); setAdditionalCharges([]); });
  }, [selBooking?.id, open]);

  // ── Auto-populate form from booking + charges ────────────────────────────
  useEffect(() => {
    if (!selBooking) return;
    const addTotal = additionalCharges.reduce((s, c) => s + (c.amount || 0), 0);
    const revised  = (selBooking.quotation || 0) + addTotal;
    const toPay    = revised - (selBooking.advance_paid || 0);
    const insYes   = selBooking.carrier_risk_opted === "YES";
    setForm(f => ({
      ...f,
      consignor_name:       selBooking.customer_name || "",
      consignor_phone:      selBooking.phone         || "",
      pickup_address:       selBooking.from_location || selBooking.moving_from || "",
      consignee_name:       selBooking.customer_name || "",
      consignee_phone:      selBooking.phone         || "",
      drop_address:         selBooking.to_location   || selBooking.moving_to   || "",
      drop_pincode:         selBooking.drop_pincode || selBooking.to_pincode || "",
      to_pay_amount:        toPay > 0 ? String(Math.round(toPay)) : "",
      insurance_opted:      selBooking.carrier_risk_opted || "NO",
      risk_coverage_type:   insYes ? "Carrier's Risk" : "Owner's Risk",
      booking_incharge:     selBooking.assigned_to_name || "",
      value_of_consignment: selBooking.consignment_value > 0 ? String(selBooking.consignment_value) : "",
      transit_insurance:    insYes,
      owner_risk:           !insYes,
      carrier_risk:         insYes,
    }));
  }, [selBooking?.id, additionalCharges]);

  // ── Reset on close ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setLoads([]);
      setAdditionalCharges([]);
      if (!initialBooking) setSelBooking(null);
    }
  }, [open]);

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  const toggleLoad = id =>
    setForm(f => {
      const previousCount = f.load_ids.length;
      const load_ids = f.load_ids.includes(id)
        ? f.load_ids.filter(x => x !== id)
        : [...f.load_ids, id];
      const quantityWasAutoFilled = f.quantity === "" || f.quantity === String(previousCount);
      return { ...f, load_ids, quantity: quantityWasAutoFilled ? String(load_ids.length) : f.quantity };
    });

  const handleDocUpload = e => {
    Array.from(e.target.files || []).forEach(file => {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} exceeds 5 MB.`); return; }
      const reader = new FileReader();
      reader.onload = evt =>
        setForm(f => ({ ...f, documents: [...f.documents, { name: file.name, data: evt.target.result }] }));
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeDoc = idx => setForm(f => ({ ...f, documents: f.documents.filter((_, i) => i !== idx) }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!selBooking)               { toast.error("Select a confirmed lead.");   return; }
    if (form.load_ids.length === 0) { toast.error("Select at least one load."); return; }

    const selectedLoads = loads.filter(l => form.load_ids.includes(l.id));
    const payload = {
      booking_id:         selBooking.id,
      customer_name:      selBooking.customer_name,
      customer_phone:     selBooking.phone || "",
      alternate_number:   selBooking.alternate_number || "",
      transfer_date:      form.transfer_date || TODAY,
      origin_type:        form.origin_type,
      origin_label:       resolveLabel(form.origin_type, form.origin_warehouse_id, selBooking, warehouses),
      destination_type:   form.destination_type,
      destination_label:  resolveLabel(form.destination_type, form.destination_warehouse_id, selBooking, warehouses),
      load_ids:           form.load_ids,
      load_details:       selectedLoads.map(l => ({ load_ref: l.load_ref, load_name: l.load_name, handle_with_care: l.handle_with_care, item_types: l.item_types || ["Household"], vehicle_item_type: l.vehicle_item_type || null })),
      consignment_id:     "",
      consignment_number: "",
      vehicle_display:    "",
      status:             "Active",
      // Consignor / Consignee
      consignor_name:     form.consignor_name,
      consignor_phone:    form.consignor_phone,
      pickup_address:     form.pickup_address,
      pickup_zone:        selBooking.moving_from || "",
      consignee_name:     form.consignee_name,
      consignee_phone:    form.consignee_phone,
      drop_address:       form.drop_address,
      drop_area:          selBooking.moving_to || "",
      drop_zone:          selBooking.moving_to || "",
      drop_pincode:       form.drop_pincode,
      start_point: form.current_location.startsWith("warehouse:") ? { type: "warehouse", warehouse_id: form.current_location.slice(10) } : { type: "from_location" },
      // Cargo
      description:        form.description,
      quantity:           form.quantity || String(form.load_ids.length),
      // Financial
      to_pay_amount:      form.to_pay_amount,
      eway_bill_no:       form.eway_bill_no,
      // Insurance
      insurance_opted:    form.insurance_opted,
      risk_coverage_type: form.risk_coverage_type,
      // Additional
      booking_incharge:   form.booking_incharge,
      documents:          form.documents.map(d => d.data || d),
      // Legacy
      value_of_consignment: form.value_of_consignment,
      freight_to_pay:       Number(form.to_pay_amount) > 0,
      amount_at_loading:    form.amount_at_loading,
      amount_at_delivery:   Number(form.to_pay_amount) > 0 ? form.to_pay_amount : "",
      transit_insurance:    form.insurance_opted === "YES",
      owner_risk:           form.risk_coverage_type === "Owner's Risk",
      carrier_risk:         form.risk_coverage_type === "Carrier's Risk",
    };

    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/consignments`, payload, { withCredentials: true });
      toast.success(`Consignment ${data.lr_number} created.`);
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create consignment.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-none p-0 gap-0 max-h-[90vh] overflow-y-auto">
        <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700] sticky top-0 z-10">
          <DialogTitle className="text-white font-bold tracking-wide text-base">Create Consignment</DialogTitle>
          <p className="text-white/60 text-xs mt-0.5">LR number auto-assigned · Fields pre-filled from Confirmed Lead</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-4">

            {/* ── Confirmed Lead ── */}
            {!initialBooking ? (
              <div>
                <FL>Confirmed Lead *</FL>
                <Select value={selBooking?.id || ""}
                  onValueChange={id => { setSelBooking(bookings.find(b => b.id === id) || null); setForm(f => ({ ...f, load_ids: [] })); }}>
                  <SelectTrigger data-testid="transfer-booking-select"
                    className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                    <SelectValue placeholder="Select a confirmed lead" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {bookings.map(b => (
                      <SelectItem key={b.id} value={b.id} className="rounded-none">
                        {b.customer_name} — {b.moving_from} → {b.moving_to}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] px-4 py-3">
                <p className="text-xs font-bold text-[#1B2A6B]">{initialBooking.customer_name}</p>
                <p className="text-[11px] text-[#475569]">
                  {initialBooking.moving_from} → {initialBooking.moving_to}
                  {initialBooking.shifting_date ? ` · Shifting: ${initialBooking.shifting_date}` : ""}
                </p>
              </div>
            )}

            {/* ── Date & LR ── */}
            <SectionTitle>Date & LR</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FL locked={!isAdmin}>Date (a)</FL>
                {isAdmin
                  ? <input type="date" value={form.transfer_date} onChange={set("transfer_date")}
                      className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 h-10 text-sm" />
                  : <ReadOnly value={form.transfer_date || TODAY} />
                }
              </div>
              <div>
                <FL>LR Number (b)</FL>
                <div className="w-full border border-dashed border-[#C7D2FE] bg-[#EEF2FF] px-3 h-10 flex items-center gap-2 text-[11px]">
                  <span className="font-mono font-bold text-[#1B2A6B]">CCL-XXXXX</span>
                  <span className="text-[#6366F1]">auto-assigned</span>
                </div>
              </div>
            </div>

            {/* ── Consignor ── */}
            <SectionTitle>Consignor Details</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><FL>Consignor Name</FL><TI value={form.consignor_name} onChange={set("consignor_name")} placeholder="Consignor full name" /></div>
              <div><FL>Consignor Phone</FL><TI value={form.consignor_phone} onChange={set("consignor_phone")} placeholder="Phone number" /></div>
              <div className="sm:col-span-2">
                <FL>Pick-up Address</FL>
                <TI value={form.pickup_address} onChange={set("pickup_address")} placeholder="Full pickup / source address" />
              </div>
            </div>

            {/* ── Consignee ── */}
            <SectionTitle>Consignee Details</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><FL>Consignee Name</FL><TI value={form.consignee_name} onChange={set("consignee_name")} placeholder="Consignee full name" /></div>
              <div><FL>Consignee Phone</FL><TI value={form.consignee_phone} onChange={set("consignee_phone")} placeholder="Phone number" /></div>
              <div>
                <FL>Drop Address</FL>
                <TI value={form.drop_address} onChange={set("drop_address")} placeholder="Full drop / destination address" />
              </div>
              <div>
                <FL>Drop Pincode</FL>
                <TI value={form.drop_pincode} onChange={set("drop_pincode")} placeholder="6-digit pincode" maxLength={6} inputMode="numeric" />
              </div>
            </div>

            <div>
              <FL>Current Location / Starting Godown</FL>
              <select aria-label="Starting location" value={form.current_location} onChange={set("current_location")} className="w-full border px-3 h-10 text-sm">
                <option value="from_location">Customer pickup location</option>
                {warehouses.map(w => <option key={w.id} value={`warehouse:${w.id}`}>{w.warehouse_name}</option>)}
              </select>
            </div>

            {/* ── Cargo & Loads ── */}
            <SectionTitle>Cargo</SectionTitle>
            <div>
              <FL>Description</FL>
              <textarea value={form.description} onChange={set("description")}
                placeholder="Describe the goods / household items being transported..."
                rows={2}
                className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2 text-sm resize-none" />
            </div>

            <div className="max-w-[220px]">
              <FL>Quantity</FL>
              <TI type="number" min="1" step="1" value={form.quantity} onChange={set("quantity")}
                placeholder="Prefilled from selected loads" />
              <p className="text-[10px] text-[#94A3B8] mt-1">Prefilled from loads, but editable.</p>
            </div>

            <div>
              <FL>
                Select Loads *{" "}
                {loads.length > 0 && (
                  <span className="font-normal normal-case tracking-normal text-[#94A3B8] ml-1">
                    ({form.load_ids.length} selected · {loads.filter(l => !l.transferred).length} available)
                  </span>
                )}
              </FL>
              {!selBooking ? (
                <p className="text-[11px] text-[#94A3B8] py-2">Select a confirmed lead first.</p>
              ) : loads.length === 0 ? (
                <p className="text-[11px] text-amber-600 py-2">No loads in this booking — add loads first.</p>
              ) : (
                <div className="border border-[#E2E8F0] p-3 space-y-2 max-h-36 overflow-y-auto">
                  {loads.map(load => {
                    const transferred = !!load.transferred;
                    return (
                      <label key={load.id}
                        className={`flex items-center gap-2.5 group ${transferred ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                        title={transferred ? `Already in ${load.lr_number || "transfer"}` : ""}>
                        <input type="checkbox" checked={form.load_ids.includes(load.id)}
                          onChange={() => !transferred && toggleLoad(load.id)}
                          disabled={transferred}
                          className="accent-[#1B2A6B] w-3.5 h-3.5 disabled:cursor-not-allowed" />
                        <span className={`text-xs font-medium ${transferred ? "text-[#94A3B8] line-through" : "text-[#0F172A]"}`}>
                          {load.load_name}
                        </span>
                        <span className="text-[10px] font-mono text-[#94A3B8]">{load.load_ref}</span>
                        {(load.item_types || ["Household"]).includes("Vehicles") && (
                          <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5">
                            {load.vehicle_item_type === "Others" || !load.vehicle_item_type ? "Vehicles" : load.vehicle_item_type}
                          </span>
                        )}
                        {transferred && (
                          <span className="ml-auto text-[9px] font-bold uppercase bg-violet-100 text-violet-700 border border-violet-200 px-1.5 py-0.5">
                            {load.lr_number || "Transferred"}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
              {form.load_ids.length > 0 && (
                <p className="text-[10px] text-[#475569] mt-1">
                  Quantity: <span className="font-bold text-[#1B2A6B]">{form.load_ids.length}</span> item{form.load_ids.length !== 1 ? "s" : ""} selected
                </p>
              )}
            </div>

            {/* ── Financial ── */}
            <SectionTitle>Financial</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <FL>To Pay</FL>
                <ReadOnly value={Number(form.to_pay_amount) > 0 ? "Yes" : "No"} />
              </div>
              <div>
                <FL>To Pay Amount (₹)</FL>
                <TI type="number" min="0" value={form.to_pay_amount} onChange={set("to_pay_amount")} placeholder="Enter amount, if applicable" />
              </div>
              <div>
                <FL>E-Way Bill No</FL>
                <TI value={form.eway_bill_no} onChange={set("eway_bill_no")} placeholder="NA" />
              </div>
            </div>

            {/* ── Insurance ── */}
            <SectionTitle>Insurance</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <FL>Insurance Opted</FL>
                <select value={form.insurance_opted}
                  onChange={e => {
                    const yes = e.target.value === "YES";
                    setForm(f => ({ ...f, insurance_opted: e.target.value, risk_coverage_type: yes ? "Carrier's Risk" : "Owner's Risk" }));
                  }}
                  className="w-full border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none h-10 px-3 text-sm rounded-none bg-white">
                  <option value="NO">NO</option>
                  <option value="YES">YES</option>
                </select>
              </div>
              <div>
                <FL>Risk Coverage</FL>
                <select value={form.risk_coverage_type} onChange={set("risk_coverage_type")}
                  className="w-full border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none h-10 px-3 text-sm rounded-none bg-white">
                  <option value="Owner's Risk">Owner's Risk</option>
                  <option value="Carrier's Risk">Carrier's Risk</option>
                </select>
              </div>
            </div>

            {/* ── Additional ── */}
            <SectionTitle>Additional</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <FL>Booking Incharge</FL>
                <TI value={form.booking_incharge} onChange={set("booking_incharge")} placeholder="RM / Booking manager name" />
              </div>
            </div>

            {/* ── Document Upload ── */}
            <div>
              <FL>Documents (ID Proof / RC Cards etc.)</FL>
              <div onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-[#E2E8F0] hover:border-[#1B2A6B] transition-colors p-4 text-center cursor-pointer group">
                <Upload size={20} className="mx-auto text-[#CBD5E1] group-hover:text-[#1B2A6B] mb-1.5 transition-colors" />
                <p className="text-xs font-semibold text-[#475569]">Click to upload documents</p>
                <p className="text-[10px] text-[#94A3B8] mt-0.5">JPG, PNG, PDF · max 5 MB each</p>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={handleDocUpload} />
              {form.documents.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {form.documents.map((doc, idx) => {
                    const name   = doc.name || `Document ${idx + 1}`;
                    const src    = doc.data  || doc;
                    const isImg  = typeof src === "string" && src.startsWith("data:image");
                    return (
                      <div key={idx} className="relative border border-[#E2E8F0] bg-[#F9FAFB] p-1.5 flex items-center gap-2">
                        {isImg
                          ? <img src={src} alt={name} className="w-12 h-12 object-cover" />
                          : <div className="w-12 h-12 bg-[#EEF2FF] flex items-center justify-center">
                              <FileText size={20} className="text-[#1B2A6B]" />
                            </div>
                        }
                        <span className="text-[10px] text-[#475569] max-w-[80px] truncate">{name}</span>
                        <button type="button" onClick={() => removeDoc(idx)}
                          className="text-[#CBD5E1] hover:text-red-500 transition-colors">
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Transport Route ── */}
            <SectionTitle>Transport Route</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: "origin",      wKey: "origin_warehouse_id",      label: "Origin *" },
                { key: "destination", wKey: "destination_warehouse_id", label: "Destination *" },
              ].map(({ key, wKey, label }) => (
                <div key={key} className="space-y-2">
                  <FL>{label}</FL>
                  <Select value={form[`${key}_type`]}
                    onValueChange={v => setForm(f => ({ ...f, [`${key}_type`]: v, [wKey]: "" }))}>
                    <SelectTrigger className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-10 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {LOCATION_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value} className="rounded-none">{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form[`${key}_type`] === "warehouse" ? (
                    <Select value={form[wKey]} onValueChange={v => setForm(f => ({ ...f, [wKey]: v }))}>
                      <SelectTrigger className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-10 text-xs">
                        <SelectValue placeholder="Select warehouse" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {warehouses.map(w => (
                          <SelectItem key={w.id} value={w.id} className="rounded-none text-xs">
                            {w.warehouse_name} — {w.city_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : selBooking && (
                    <p className="text-[11px] text-[#475569] bg-[#F8FAFC] px-3 py-2 border border-[#E2E8F0]">
                      {resolveLabel(form[`${key}_type`], "", selBooking, warehouses) || "—"}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <p className="text-xs text-slate-500">After creating this LR, use Manage Movement to choose a Trip Sheet and its drop point.</p>

          </div>

          <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2 sticky bottom-0">
            <button type="button" onClick={onClose}
              className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">
              Cancel
            </button>
            <Button type="submit" disabled={saving} data-testid="submit-transfer-btn"
              className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
              {saving ? "Creating..." : "Create Consignment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
